import { useMemo, useState } from "react"
import { NavLink, useNavigate, useParams } from "react-router"
import {
  AlertCircle,
  Building01,
  Building07,
  CalendarDate,
  CheckCircle,
  ChevronDown,
  CurrencyDollar,
  Flag05,
  LayersThree01,
  QrCode01,
  Scales02,
  SearchLg,
  Tool01,
  Users01,
  Zap,
} from "@untitledui/icons"
import { motion, AnimatePresence } from "motion/react"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { useConnectedBodyshop, useTeam, useTeamInvites } from "@/lib/api/use-teams"
import { useOrganization } from "@/lib/organization/organization-context"
import { useTeamRole } from "@/lib/team/use-current-role"
import { feesNotReviewed } from "@/lib/fees/defaults"
import { resolveIntakeConfig } from "@/lib/intake/intake-config"
import { cx } from "@/lib/utils/cx"

// Compact labels for the role chip in each entity row. Mirrors the
// abbreviation map in TeamSwitcher so the role vocabulary stays
// consistent between the topbar switcher and the Settings sidebar's
// entity picker.
const ROLE_BADGE_LABEL: Record<string, string> = {
  Director: "Director",
  "General Manager": "GM",
  "Service Manager": "SM",
  "Service Advisor": "SA",
  "Sales Manager": "Sales Mgr",
  "Sales Representative": "Sales Rep",
  Estimator: "Estimator",
}

// Settings sidebar — entity selector at top, section list below.
//
// Visibility = entity ownership (only teams the user belongs to).
// Per-action authorization happens inside each panel via
// useTeamPermissions(teamId).
//
// Picking an entity in this sidebar ALSO updates OrganizationContext
// so the topbar TeamSwitcher reflects the same scope. Single source
// of truth, two surfaces editing it. The opposite direction (topbar
// → URL) is handled by the SettingsPage router via useEffect.

type TeamKind = "group" | "dealership" | "bodyshop"

interface Section {
  id: string
  label: string
  icon: React.ComponentType<{ className?: string }>
  // Drives the trailing status icon. "members" → "N pending invites".
  // "fees" → "rates still on placeholder". "body-shop" → "no shop
  // connected". "network" → "no dealerships in group yet". "intake" →
  // "intake off or link never shared".
  statusKey?: "members" | "fees" | "body-shop" | "network" | "marketing" | "intake"
}

const GROUP_SECTIONS: Section[] = [
  { id: "business-info", label: "Business info",     icon: Building07 },
  { id: "members",       label: "Members & invites", icon: Users01, statusKey: "members" },
  { id: "network",       label: "Network",           icon: LayersThree01, statusKey: "network" },
]

const DEALERSHIP_SECTIONS: Section[] = [
  { id: "business-info", label: "Business info",     icon: Building01 },
  { id: "members",       label: "Members & invites", icon: Users01, statusKey: "members" },
  { id: "service-lane",  label: "Service lane",      icon: Scales02 },
  { id: "goals",         label: "Goals & rewards",   icon: Flag05 },
  { id: "body-shop",     label: "Body shop",         icon: Tool01,  statusKey: "body-shop" },
]

const BODYSHOP_SECTIONS: Section[] = [
  { id: "business-info", label: "Business info",     icon: Tool01 },
  { id: "members",       label: "Members & invites", icon: Users01, statusKey: "members" },
  { id: "fees",          label: "Rates & fees",      icon: CurrencyDollar, statusKey: "fees" },
  { id: "intake",        label: "Consumer intake",   icon: QrCode01, statusKey: "intake" },
  { id: "marketing",     label: "Marketing Supercharge", icon: Zap, statusKey: "marketing" },
  { id: "booking",       label: "Booking calendar",  icon: CalendarDate },
  { id: "dealers",       label: "Your dealers",      icon: Building01 },
]

function sectionsFor(kind: TeamKind): Section[] {
  if (kind === "group") return GROUP_SECTIONS
  if (kind === "bodyshop") return BODYSHOP_SECTIONS
  return DEALERSHIP_SECTIONS
}

const KIND_TILE: Record<TeamKind, { gradient: string; shadow: string; icon: typeof Building01; label: string }> = {
  group: {
    gradient: "from-indigo-500 to-indigo-700",
    shadow: "shadow-indigo-600/25",
    icon: LayersThree01,
    label: "Group",
  },
  dealership: {
    gradient: "from-sky-500 to-sky-700",
    shadow: "shadow-sky-600/25",
    icon: Building01,
    label: "Dealership",
  },
  bodyshop: {
    gradient: "from-amber-500 to-amber-700",
    shadow: "shadow-amber-600/25",
    icon: Tool01,
    label: "Body shop",
  },
}

interface EntityChoice {
  id: string
  name: string
  kind: TeamKind
  city?: string
  state?: string
}

export function SettingsSidebar() {
  const params = useParams()
  const navigate = useNavigate()
  const {
    activeGroup,
    availableDealerships,
    availableBodyShops,
    setActiveGroup,
    setGroupView,
    setActiveTeam,
  } = useOrganization()

  // Entity list = teams the caller is a DIRECT member of (from GET /v1/teams).
  // Do NOT use groupDealerships here — that endpoint returns the full group
  // org-chart (every child team) regardless of caller membership, so a
  // Director who's a member of only 4 of 10 child dealerships would see
  // all 10 and get 403 on the 6 they aren't a member of.
  // OrgChartPanel (Group → Network) is the correct surface for the full
  // org-chart view; entity-scoped settings panels need actual memberships.
  const entities: EntityChoice[] = useMemo(() => {
    const out: EntityChoice[] = []
    if (activeGroup) {
      out.push({
        id: String(activeGroup.id),
        name: activeGroup.name,
        kind: "group",
        city: activeGroup.city,
        state: activeGroup.state,
      })
    }
    for (const d of availableDealerships) {
      out.push({
        id: String(d.id),
        name: d.name,
        kind: "dealership",
        city: d.city,
        state: d.state,
      })
    }
    for (const b of availableBodyShops) {
      out.push({
        id: String(b.id),
        name: b.name,
        kind: "bodyshop",
        city: b.city,
        state: b.state,
      })
    }
    return out
  }, [activeGroup, availableDealerships, availableBodyShops])

  const urlKind = params.kind as TeamKind | undefined
  const urlTeamId = params.teamId
  const activeEntity =
    urlKind && urlTeamId
      ? entities.find((e) => e.kind === urlKind && e.id === urlTeamId)
      : entities[0]

  const sections = activeEntity ? sectionsFor(activeEntity.kind) : []
  const currentSection = params.section ?? "members"

  const handlePickEntity = (entity: EntityChoice) => {
    // Picking in Settings ALSO flips operational scope so the topbar
    // switcher stays in sync — single source of truth.
    if (entity.kind === "group") {
      setActiveGroup(entity.id)
      setGroupView(true)
    } else {
      setActiveTeam(entity.kind, entity.id)
      setGroupView(false)
    }
    // Keep the user on the same section if the new kind serves it,
    // otherwise fall back to business-info (every kind has it). Matches
    // the cross-kind fallback logic in settings.tsx → safeSectionFor.
    const newSections = sectionsFor(entity.kind)
    const target = newSections.some((s) => s.id === currentSection)
      ? currentSection
      : "business-info"
    navigate(`/settings/${entity.kind}/${entity.id}/${target}`)
  }

  if (!activeEntity) return null

  return (
    <nav aria-label="Settings sections" className="flex flex-col gap-5">
      <EntitySelector
        entity={activeEntity}
        entities={entities}
        onPick={handlePickEntity}
      />

      <div className="flex flex-col gap-1">
        {sections.map((section) => (
          <SectionRow
            key={section.id}
            to={`/settings/${activeEntity.kind}/${activeEntity.id}/${section.id}`}
            icon={section.icon}
            label={section.label}
            statusKey={section.statusKey}
            kind={activeEntity.kind}
            teamId={activeEntity.id}
          />
        ))}
      </div>
    </nav>
  )
}

function EntitySelector({
  entity,
  entities,
  onPick,
}: {
  entity: EntityChoice
  entities: EntityChoice[]
  onPick: (entity: EntityChoice) => void
}) {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState("")
  const tile = KIND_TILE[entity.kind]
  const Icon = tile.icon
  const cs = [entity.city, entity.state].filter(Boolean).join(", ")
  const isMulti = entities.length > 1

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase()
    if (!q) return entities
    return entities.filter(
      (e) =>
        e.name.toLowerCase().includes(q) ||
        (e.city ?? "").toLowerCase().includes(q) ||
        (e.state ?? "").toLowerCase().includes(q),
    )
  }, [entities, query])

  const showSearch = entities.length > 5

  const cardClass = cx(
    "rounded-2xl border p-3.5 flex items-center gap-3 transition-all duration-150",
    isMulti && "cursor-pointer hover:border-zinc-300",
  )

  const inner = (
    <>
      <div
        className={cx(
          "size-12 rounded-2xl bg-gradient-to-br grid place-items-center shrink-0 shadow-md",
          tile.gradient,
          tile.shadow,
        )}
      >
        <Icon className="size-[22px] text-white" aria-hidden />
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-[10.5px] font-bold uppercase tracking-[0.14em] text-zinc-400 leading-tight">
          {tile.label}
        </p>
        <p className="text-[14px] font-semibold text-zinc-900 truncate mt-1 leading-tight">
          {entity.name}
        </p>
        {cs && (
          <p className="text-[11.5px] text-zinc-500 truncate mt-0.5 leading-tight">
            {cs}
          </p>
        )}
      </div>
      {isMulti && (
        <ChevronDown className="size-4 text-zinc-400 shrink-0" aria-hidden />
      )}
    </>
  )

  if (!isMulti) {
    return (
      <div className={cx(cardClass, "border-zinc-200/70 bg-white")}>{inner}</div>
    )
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={cx(
            cardClass,
            "w-full text-left border-zinc-200/70 bg-white",
            "data-[state=open]:border-emerald-300 data-[state=open]:ring-4 data-[state=open]:ring-emerald-500/10",
          )}
        >
          {inner}
        </button>
      </PopoverTrigger>
      <PopoverContent
        side="bottom"
        align="start"
        sideOffset={8}
        className="w-[calc(var(--radix-popover-trigger-width))] min-w-[300px] p-0 rounded-3xl border border-zinc-200/70 bg-white shadow-2xl shadow-zinc-900/[0.12] overflow-hidden"
      >
        <AnimatePresence>
          <motion.div
            key="picker"
            initial={{ opacity: 0, y: -6, scale: 0.985 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{
              type: "spring",
              stiffness: 320,
              damping: 28,
              opacity: { duration: 0.15 },
            }}
          >
            <div className="bg-gradient-to-br from-zinc-50/60 via-white to-emerald-50/30 px-5 pt-4 pb-3 border-b border-zinc-100">
              <p className="text-[10.5px] font-bold uppercase tracking-[0.16em] text-emerald-600">
                Switch location
              </p>
              <p className="text-[13px] text-zinc-500 mt-1 leading-snug">
                Pick which place you're configuring.
              </p>
            </div>

            {showSearch && (
              <div className="px-3 pt-3 pb-1">
                <div className="relative">
                  <SearchLg
                    className="absolute left-3.5 top-1/2 -translate-y-1/2 size-4 text-zinc-400"
                    aria-hidden
                  />
                  <input
                    autoFocus
                    placeholder="Search locations..."
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    className="w-full h-11 rounded-xl border border-zinc-200 bg-white pl-10 pr-3 text-[14px] text-zinc-900 placeholder:text-zinc-400 outline-none focus:border-emerald-500 focus:ring-4 focus:ring-emerald-500/10 transition-all duration-150"
                  />
                </div>
              </div>
            )}
            <div className="max-h-[420px] overflow-y-auto py-2">
              {filtered.length === 0 ? (
                <p className="px-5 py-6 text-center text-[12.5px] text-zinc-400">
                  No locations match "{query}"
                </p>
              ) : (
                <SelectorRows
                  entities={filtered}
                  active={entity}
                  onPick={(e) => {
                    setOpen(false)
                    onPick(e)
                  }}
                />
              )}
            </div>
          </motion.div>
        </AnimatePresence>
      </PopoverContent>
    </Popover>
  )
}

function SelectorRows({
  entities,
  active,
  onPick,
}: {
  entities: EntityChoice[]
  active: EntityChoice
  onPick: (e: EntityChoice) => void
}) {
  const groups: { label: string; items: EntityChoice[] }[] = [
    { label: "Group", items: entities.filter((e) => e.kind === "group") },
    { label: "Dealerships", items: entities.filter((e) => e.kind === "dealership") },
    { label: "Body shops", items: entities.filter((e) => e.kind === "bodyshop") },
  ].filter((g) => g.items.length > 0)

  return (
    <>
      {groups.map((group, gi) => (
        <div
          key={group.label}
          className={cx("py-2 px-2", gi > 0 && "border-t border-zinc-100")}
        >
          <p className="px-2.5 pb-1.5 pt-1 text-[10px] font-bold uppercase tracking-[0.14em] text-zinc-400">
            {group.label}
          </p>
          <div className="flex flex-col gap-1">
            {group.items.map((entity) => {
              const tile = KIND_TILE[entity.kind]
              const Icon = tile.icon
              const cs = [entity.city, entity.state].filter(Boolean).join(", ")
              const isActive =
                entity.kind === active.kind && entity.id === active.id
              return (
                <button
                  key={`${entity.kind}-${entity.id}`}
                  type="button"
                  onClick={() => onPick(entity)}
                  className={cx(
                    "w-full flex items-center gap-3 p-2.5 rounded-2xl text-left border transition-all duration-150",
                    isActive
                      ? "border-emerald-200/80 bg-emerald-50/70 ring-1 ring-emerald-200/60"
                      : "border-transparent hover:border-zinc-200 hover:bg-zinc-50/60",
                  )}
                >
                  <div
                    className={cx(
                      "size-10 rounded-2xl bg-gradient-to-br grid place-items-center shrink-0 shadow-md",
                      tile.gradient,
                      tile.shadow,
                    )}
                  >
                    <Icon className="size-[18px] text-white" aria-hidden />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p
                      className={cx(
                        "text-[13.5px] font-semibold truncate leading-tight",
                        isActive ? "text-emerald-900" : "text-zinc-900",
                      )}
                    >
                      {entity.name}
                    </p>
                    <p
                      className={cx(
                        "text-[11.5px] truncate mt-0.5 leading-tight",
                        isActive ? "text-emerald-700/80" : "text-zinc-500",
                      )}
                    >
                      {cs || tile.label}
                    </p>
                  </div>
                  <RoleBadge teamId={Number(entity.id)} isActive={isActive} />
                  {isActive && (
                    <div className="size-7 rounded-full bg-emerald-500 grid place-items-center shrink-0 shadow-sm shadow-emerald-600/30">
                      <CheckCircle
                        className="size-4 text-white"
                        strokeWidth={2.5}
                        aria-hidden
                      />
                    </div>
                  )}
                </button>
              )
            })}
          </div>
        </div>
      ))}
    </>
  )
}

function SectionRow({
  to,
  icon: Icon,
  label,
  kind,
  statusKey,
  teamId,
}: {
  to: string
  icon: React.ComponentType<{ className?: string }>
  label: string
  kind: TeamKind
  statusKey?: Section["statusKey"]
  teamId: string
}) {
  const status = useSectionStatus(teamId, kind, statusKey)
  return (
    <NavLink
      to={to}
      end
      className={({ isActive }) =>
        cx(
          "group relative flex items-center gap-2.5 px-3.5 py-2.5 rounded-xl text-[14px] font-medium transition-all duration-150",
          isActive
            ? "bg-emerald-50 text-emerald-900 font-semibold ring-1 ring-emerald-200/60"
            : "text-zinc-600 hover:bg-zinc-100 hover:text-zinc-900",
        )
      }
    >
      {({ isActive }) => (
        <>
          <Icon
            className={cx(
              "size-[16px] shrink-0 transition-colors",
              isActive ? "text-emerald-600" : "text-zinc-400",
            )}
          />
          <span className="truncate flex-1">{label}</span>
          {status && <StatusGlyph tone={status.tone} tooltip={status.tooltip} />}
        </>
      )}
    </NavLink>
  )
}

// Per-row role chip — same shape as the TeamSwitcher's RoleBadge but
// scoped to this file (the surfaces have different active-state colors).
// React Query dedupes /v1/teams/:id/members across both surfaces by
// query key, so the popover and the topbar switcher share a cache.
function RoleBadge({ teamId, isActive }: { teamId: number; isActive: boolean }) {
  const role = useTeamRole(teamId)
  if (!role) return null
  const label = ROLE_BADGE_LABEL[role] ?? role
  return (
    <span
      className={cx(
        "inline-flex items-center px-1.5 py-0.5 rounded text-[9.5px] font-bold uppercase tracking-wide whitespace-nowrap shrink-0",
        isActive ? "bg-emerald-100/80 text-emerald-800" : "bg-zinc-100 text-zinc-600",
      )}
    >
      {label}
    </span>
  )
}

function StatusGlyph({
  tone,
  tooltip,
}: {
  tone: "alert" | "ok"
  tooltip: string
}) {
  if (tone === "alert") {
    return (
      <span title={tooltip} className="shrink-0">
        <AlertCircle className="size-[15px] text-amber-500" aria-label={tooltip} />
      </span>
    )
  }
  return (
    <span title={tooltip} className="shrink-0">
      <CheckCircle className="size-[15px] text-emerald-500" aria-label={tooltip} />
    </span>
  )
}

interface Status {
  tooltip: string
  tone: "alert" | "ok"
}

function useSectionStatus(
  teamId: string,
  kind: TeamKind,
  statusKey: Section["statusKey"],
): Status | null {
  const numericTeamId = Number(teamId)
  const { activeGroup, groupDealerships } = useOrganization()
  const { data: invites } = useTeamInvites(
    statusKey === "members" ? numericTeamId : null,
  )
  const { data: team } = useTeam(
    statusKey === "fees" || statusKey === "marketing" || statusKey === "intake"
      ? teamId
      : null,
  )
  const { data: connectedShop } = useConnectedBodyshop(
    statusKey === "body-shop" && kind === "dealership" ? teamId : null,
  )

  if (!statusKey) return null

  if (statusKey === "members") {
    const count = (invites ?? []).length
    if (count > 0) {
      return {
        tooltip: `${count} unaccepted invite${count === 1 ? "" : "s"}`,
        tone: "alert",
      }
    }
    return null
  }

  if (statusKey === "fees") {
    if (team && feesNotReviewed(team.metadata)) {
      return {
        tooltip: "Rates are still on placeholder defaults",
        tone: "alert",
      }
    }
    return null
  }

  // Paused follow-up means no automated texts/emails at all — worth an amber
  // glyph so it isn't discovered weeks later (#73).
  if (statusKey === "marketing") {
    const enabled =
      (team?.metadata as { marketingSupercharge?: { enabled?: boolean } } | undefined)
        ?.marketingSupercharge?.enabled ?? true
    if (team && !enabled) {
      return {
        tooltip: "Follow-up is paused — no automated messages are going out",
        tone: "alert",
      }
    }
    return null
  }

  // Consumer intake earns its quiet glyph only once the shop actually put
  // the link somewhere — enabled but never shared is a front door nobody
  // can find, and paused means no leads at all.
  if (statusKey === "intake") {
    if (!team) return null
    const intake = resolveIntakeConfig(
      (team.metadata as Record<string, unknown> | null)?.intake,
    )
    if (!intake.enabled) {
      return {
        tooltip: "Consumer intake is paused — no one can send you damage photos",
        tone: "alert",
      }
    }
    if (!intake.sharedAt) {
      return {
        tooltip: "Your consumer intake link hasn't been shared yet",
        tone: "alert",
      }
    }
    return null
  }

  if (statusKey === "body-shop") {
    if (kind === "dealership" && !connectedShop) {
      return {
        tooltip: "No body shop connected — estimates can't ship yet",
        tone: "alert",
      }
    }
    return null
  }

  if (statusKey === "network") {
    if (
      kind === "group" &&
      activeGroup &&
      String(activeGroup.id) === teamId &&
      groupDealerships.length === 0
    ) {
      return {
        tooltip: "Your group has no dealerships yet — add one to get started",
        tone: "alert",
      }
    }
    return null
  }

  return null
}
